import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Plug,
  RefreshCw,
  Unplug,
} from 'lucide-react';

import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDisconnectGoogleConnection } from '@/hooks/mutations/google-connections/use-google-connection-mutations';
import { useGoogleConnections } from '@/hooks/queries/google-connections/use-google-connections';
import { GoogleConnectionsError } from '@/services/google-connections/google-connections';
import { GoogleService } from '@/services/google-connections/types';
import { useOrgStore } from '@/stores/org-store';
import { formatDateTime } from '@/utils/format-date';

import {
  ALL_CATEGORIES,
  Connector,
  ConnectorEntry,
  FILTER_MIN,
  availableCategories,
  filterAvailable,
  groupConnectors,
  needsReconnect,
} from './helpers/connectors';
import { ConnectorsSkeleton } from './connectors-skeleton';
import { useGoogleConnectPopup } from './use-google-connect-popup';

const RECONNECT_LABEL: Record<string, string> = {
  expired: 'Expired',
  failed: 'Failed',
  inactive: 'Inactive',
};

type CardActions = {
  connecting: boolean;
  disconnecting: boolean;
  onConnect: () => void;
  onCancelPending: () => void;
};

const ConnectorIcon = ({ connector }: { connector: Connector }) => (
  <div className='flex size-10 shrink-0 items-center justify-center rounded-md bg-muted/50'>
    <connector.Icon size={20} />
  </div>
);

const PendingNotice = ({
  children,
  disabled,
  onCancel,
}: {
  children: string;
  disabled: boolean;
  onCancel: () => void;
}) => (
  <div className='flex items-center justify-between gap-3 rounded-md border py-1.5 pl-3 pr-1.5 text-xs text-muted-foreground'>
    <span>{children}</span>
    <Button variant='ghost' size='sm' onClick={onCancel} disabled={disabled}>
      Cancel pending
    </Button>
  </div>
);

const LinkedRow = ({
  entry: { connector, row },
  connecting,
  disconnecting,
  onConnect,
  onCancelPending,
  onDisconnect,
}: CardActions & { entry: ConnectorEntry; onDisconnect: () => void }) => {
  const broken = needsReconnect(row);
  const busy = connecting || disconnecting;
  const since = row.connected_at && formatDateTime(row.connected_at);
  const reconnectIcon = connecting ? (
    <Loader2 className='size-4 animate-spin' />
  ) : (
    <RefreshCw className='size-4' />
  );

  return (
    <div className='flex items-center gap-4 px-5 py-4'>
      <ConnectorIcon connector={connector} />
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-sm font-medium'>{connector.name}</span>
          {broken ? (
            <Badge variant='destructive' className='gap-1'>
              <AlertCircle className='size-3' />
              {RECONNECT_LABEL[row.status] ?? 'Needs reconnecting'}
            </Badge>
          ) : (
            <Badge variant='secondary' className='gap-1'>
              <CheckCircle2 className='size-3' />
              Connected
            </Badge>
          )}
          {row.pending_authorization && (
            <Badge variant='secondary'>Reauthorization in flight</Badge>
          )}
        </div>
        <div className='truncate text-[13px] text-muted-foreground'>
          <span className='text-foreground'>
            {row.connected_as || 'Unknown account'}
          </span>
          {since && ` · since ${since}`}
        </div>
        {row.pending_authorization && (
          <div className='mt-1.5'>
            <PendingNotice disabled={busy} onCancel={onCancelPending}>
              {`A reauthorization link is in flight (links expire after ~10 minutes). Finish the ${connector.provider} consent, or cancel it.`}
            </PendingNotice>
          </div>
        )}
      </div>
      <div className='flex shrink-0 gap-2'>
        <Button
          variant={broken ? 'default' : 'outline'}
          size='sm'
          onClick={onConnect}
          disabled={busy}
        >
          {reconnectIcon}
          Reconnect
        </Button>
        <Button
          variant='outline'
          size='sm'
          className='text-destructive hover:text-destructive'
          onClick={onDisconnect}
          disabled={busy}
        >
          {disconnecting ? (
            <Loader2 className='size-4 animate-spin' />
          ) : (
            <Unplug className='size-4' />
          )}
          Disconnect
        </Button>
      </div>
    </div>
  );
};

const AvailableCard = ({
  entry: { connector, row },
  connecting,
  disconnecting,
  onConnect,
  onCancelPending,
}: CardActions & { entry: ConnectorEntry }) => {
  const pending = row.status === 'pending';

  return (
    <div className='flex flex-col gap-3 rounded-lg border bg-card p-5'>
      <div className='flex items-center gap-3'>
        <ConnectorIcon connector={connector} />
        <div className='min-w-0'>
          <div className='text-sm font-medium'>{connector.name}</div>
          <div className='text-xs text-muted-foreground'>
            {connector.category}
          </div>
        </div>
        {pending && (
          <Badge variant='outline' className='ml-auto gap-1 self-start'>
            <AlertCircle className='size-3' />
            Authorization pending
          </Badge>
        )}
      </div>
      <p className='flex-1 text-[13px] leading-[18px] text-muted-foreground'>
        {connector.description}
      </p>
      <Button
        variant='outline'
        size='sm'
        className='self-start'
        onClick={onConnect}
        disabled={connecting || disconnecting}
      >
        {connecting ? (
          <Loader2 className='size-4 animate-spin' />
        ) : (
          <Plug className='size-4' />
        )}
        {connecting
          ? `Waiting for ${connector.provider}…`
          : `Connect ${connector.name}`}
      </Button>
      {pending && (
        <PendingNotice
          disabled={connecting || disconnecting}
          onCancel={onCancelPending}
        >
          {`Link in flight, expires after ~10 minutes. Finish the ${connector.provider} consent, or cancel it.`}
        </PendingNotice>
      )}
    </div>
  );
};

const unavailableMessage = (error: unknown) =>
  error instanceof GoogleConnectionsError && error.statusCode === 404
    ? 'Connectors are not available on this workspace.'
    : 'Could not load your connectors.';

export const ConnectorsPage = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const notification = useNotification();
  const query = useGoogleConnections(orgId);
  const { connect, isConnecting } = useGoogleConnectPopup(orgId);
  const disconnectMutation = useDisconnectGoogleConnection(orgId);
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState<Connector | null>(null);

  const { linked, available } = useMemo(
    () => groupConnectors(query.data?.services),
    [query.data],
  );
  const showFilters = available.length >= FILTER_MIN;
  const shownAvailable = showFilters
    ? filterAvailable(available, category, search)
    : available;
  const noMatches = showFilters && shownAvailable.length === 0;

  const revoke = async (service: GoogleService, pendingOnly: boolean) => {
    try {
      await disconnectMutation.mutateAsync({ service, pendingOnly });
      notification.success?.(
        pendingOnly ? 'Pending authorization canceled' : 'Account disconnected',
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: { orgId, service, pendingOnly },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not disconnect',
      );
    }
  };

  const actionsFor = (service: GoogleService): CardActions => ({
    connecting: isConnecting(service),
    disconnecting:
      disconnectMutation.isPending &&
      disconnectMutation.variables?.service === service,
    onConnect: () => void connect(service),
    onCancelPending: () => void revoke(service, true),
  });

  let content;
  if (query.isLoading) {
    content = <ConnectorsSkeleton />;
  } else if (query.isError) {
    content = (
      <div className='flex items-center justify-between gap-3 rounded-md border border-destructive/40 p-4 text-sm text-muted-foreground'>
        {unavailableMessage(query.error)}
        <Button
          variant='outline'
          size='sm'
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          Try again
        </Button>
      </div>
    );
  } else {
    content = (
      <>
        <section className='flex flex-col gap-3'>
          <div className='flex items-center gap-2'>
            <h2 className='text-base font-medium'>Connected</h2>
            <Badge variant='secondary'>{linked.length}</Badge>
          </div>
          {linked.length === 0 ? (
            <div className='rounded-lg border border-dashed p-6 text-sm text-muted-foreground'>
              No connectors yet. Pick one below, or ask the agent in chat.
            </div>
          ) : (
            <div className='divide-y overflow-hidden rounded-lg border bg-card shadow-xs'>
              {linked.map((entry) => (
                <LinkedRow
                  key={entry.connector.id}
                  entry={entry}
                  {...actionsFor(entry.connector.id)}
                  onDisconnect={() => setConfirming(entry.connector)}
                />
              ))}
            </div>
          )}
        </section>

        {available.length > 0 && (
          <section className='flex flex-col gap-4'>
            <div className='flex min-h-9 flex-wrap items-center justify-between gap-4'>
              <h2 className='text-base font-medium'>Available</h2>
              {showFilters && (
                <div className='w-full sm:w-64'>
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder='Search connectors'
                    aria-label='Search connectors'
                  />
                </div>
              )}
            </div>
            {showFilters && (
              <Tabs value={category} onValueChange={setCategory}>
                <TabsList className='h-auto flex-wrap'>
                  {availableCategories(available).map((label) => (
                    <TabsTrigger key={label} value={label}>
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
              {shownAvailable.map((entry) => (
                <AvailableCard
                  key={entry.connector.id}
                  entry={entry}
                  {...actionsFor(entry.connector.id)}
                />
              ))}
            </div>
            {noMatches && (
              <p className='py-3 text-sm text-muted-foreground'>
                No connectors match your search.
              </p>
            )}
          </section>
        )}
      </>
    );
  }

  return (
    <div className='w-full'>
      <div className='flex max-w-5xl flex-col gap-8'>
        <div>
          <PageTitle title='Connectors' />
          <PageDescription>
            Connect the services your workflows and chat can use on your behalf.
            Consent runs on each provider&apos;s own screen; Capitol never sees
            your password, and tokens are held and refreshed by the managed
            OAuth provider. You can also do this from chat — just ask the agent
            to connect a service.
          </PageDescription>
        </div>
        {content}
      </div>

      <Modal
        isDialogOpen={!!confirming}
        setIsDialogOpen={(open) => !open && setConfirming(null)}
        title={`Disconnect ${confirming?.name ?? ''}`}
        description={
          `This revokes the ${confirming?.name} grant held for your account. ` +
          `Scheduled workflows that use your ${confirming?.name} stop ` +
          `working until you reconnect.`
        }
        footer={
          <>
            <Button variant='outline' onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                if (confirming) void revoke(confirming.id, false);
                setConfirming(null);
              }}
            >
              <Unplug className='size-4' /> Disconnect
            </Button>
          </>
        }
      />
    </div>
  );
};
